// frontend/src/features/livestock/ZoomControls.tsx
// Zoom out, the scale (a press resets it), zoom in: about the middle of the
// view. Under compare's synced zoom and in the photo viewer
// (docs/plans/2026-10-29-photo-lightbox-zoom.md).

import { ActionIcon, Button, Group } from '@mantine/core';
import { IconZoomIn, IconZoomOut } from '@tabler/icons-react';
import {
  NO_ZOOM,
  ZOOM_MAX,
  ZOOM_STEP,
  isZoomed,
  zoomAt,
  zoomLabel,
  type CompareZoom,
  type ZoomLimits,
} from './compareZoom';
import classes from './ZoomControls.module.css';

export interface ZoomControlsProps {
  zoom: CompareZoom;
  /** `animate`: a step from a button, which the viewer eases. */
  onZoomChange: (zoom: CompareZoom, animate: boolean) => void;
  /** On the dark full-screen ground. */
  dark?: boolean;
  limits?: ZoomLimits;
  /** Names the group: "Zoom before" when each side of compare has its own. */
  label?: string;
}

export function ZoomControls({
  zoom,
  onZoomChange,
  dark = false,
  limits,
  label = 'Zoom',
}: ZoomControlsProps): JSX.Element {
  const color = dark ? 'gray.0' : 'gray';
  return (
    <Group
      gap={4}
      justify="center"
      wrap="nowrap"
      role="group"
      aria-label={label}
      className={classes.zoomControls}
      data-dark={dark || undefined}
    >
      <ActionIcon
        variant="subtle"
        color={color}
        className={classes.zoomButton}
        size={44}
        aria-label="Zoom out"
        disabled={!isZoomed(zoom)}
        onClick={() => onZoomChange(zoomAt(zoom, 1 / ZOOM_STEP, 0.5, 0.5, limits), true)}
      >
        <IconZoomOut size={16} />
      </ActionIcon>
      <Button
        variant="subtle"
        color={color}
        className={classes.zoomButton}
        h={44}
        px="xs"
        miw={56}
        aria-label={`Reset zoom, now ${zoomLabel(zoom)}`}
        disabled={!isZoomed(zoom)}
        onClick={() => onZoomChange(NO_ZOOM, true)}
        styles={{ label: { fontVariantNumeric: 'tabular-nums' } }}
      >
        {zoomLabel(zoom)}
      </Button>
      <ActionIcon
        variant="subtle"
        color={color}
        className={classes.zoomButton}
        size={44}
        aria-label="Zoom in"
        disabled={zoom.scale >= (limits?.max ?? ZOOM_MAX)}
        onClick={() => onZoomChange(zoomAt(zoom, ZOOM_STEP, 0.5, 0.5, limits), true)}
      >
        <IconZoomIn size={16} />
      </ActionIcon>
    </Group>
  );
}
